import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Label } from '@apps/web/components/ui/label';

type ClientIdentifyingInfoConfirmationDialogProps = {
    readonly open: boolean;
    readonly clientReferenceTerm: 'client' | 'patient';
    /** Identifying variables the author used, e.g. `client_first_name`. */
    readonly identifyingVariables: ReadonlyArray<string>;
    readonly confirmLabel?: string;
    readonly onOpenChange: (open: boolean) => void;
    readonly onConfirm: () => void;
};

export function ClientIdentifyingInfoConfirmationDialog({
    open,
    clientReferenceTerm,
    identifyingVariables,
    confirmLabel = 'Save template',
    onOpenChange,
    onConfirm,
}: ClientIdentifyingInfoConfirmationDialogProps) {
    const [accepted, setAccepted] = useState(false);
    const clientReferenceTermTitle =
        clientReferenceTerm.charAt(0).toUpperCase() +
        clientReferenceTerm.slice(1);

    const close = () => {
        setAccepted(false);
        onOpenChange(false);
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) {
                    setAccepted(false);
                }
                onOpenChange(nextOpen);
            }}
        >
            <DialogContent>
                <DialogHeader className="text-left">
                    <DialogTitle className="flex items-center gap-2">
                        <TriangleAlert className="size-5 shrink-0 text-amber-600 dark:text-amber-400" />
                        {clientReferenceTermTitle}-identifying information
                    </DialogTitle>
                    <DialogDescription asChild={true} className="text-left">
                        <div className="space-y-3">
                            <p>
                                {`We strongly discourage including personally identifiable information (PII), such as a ${clientReferenceTerm}’s name or address, in SMS messages, which are not\u00a0encrypted.`}
                            </p>
                            <div className="space-y-2">
                                <p>
                                    {`These variables in your message send ${clientReferenceTerm}-identifying information:`}
                                </p>
                                <ul className="flex flex-wrap gap-1.5">
                                    {identifyingVariables.map((variable) => (
                                        <li key={variable}>
                                            <code className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-xs text-amber-950 dark:bg-amber-950 dark:text-amber-200">
                                                {`{${variable}}`}
                                            </code>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <p>
                                {`By checking this box, I confirm I am entering ${clientReferenceTerm}-identifying information and accept liability for any resulting disclosure.`}
                            </p>
                        </div>
                    </DialogDescription>
                </DialogHeader>
                <div className="flex items-start gap-3 rounded-md border p-3">
                    <Checkbox
                        id="accept-client-identifying-info-liability"
                        checked={accepted}
                        onCheckedChange={(checked) =>
                            setAccepted(checked === true)
                        }
                    />
                    <Label
                        htmlFor="accept-client-identifying-info-liability"
                        className="cursor-pointer leading-4"
                    >
                        Proceed and accept liability
                    </Label>
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={close}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        disabled={!accepted}
                        onClick={() => {
                            setAccepted(false);
                            onConfirm();
                        }}
                    >
                        {confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
